import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Shield, Users, Wallet, Trophy, BarChart3, Search,
  CheckCircle, XCircle, Clock, Ban, Crown, RefreshCw,
  TrendingUp, DollarSign, Gamepad2, ArrowUpRight, ArrowDownRight,
  PieChart, Activity, Calendar, Target
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#00E701', '#007AFF', '#FFD600', '#FF3B30', '#FF007A', '#00F0FF'];

export const AdminPage = () => {
  const { user, token } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [totalUserPages, setTotalUserPages] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchDashboard();
    fetchUsers();
    fetchPendingWithdrawals();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1, search = "") => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { page, limit: 10, search: search || undefined },
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
      setTotalUserPages(res.data.pages);
      setUserPage(page);
    } catch (error) {
      console.error("Fetch users error:", error);
    }
  };

  const fetchPendingWithdrawals = async () => {
    try {
      const res = await axios.get(`${API}/admin/withdrawals/pending`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingWithdrawals(res.data.withdrawals || []);
    } catch (error) {
      console.error("Fetch withdrawals error:", error);
    }
  };

  const handleWithdrawal = async (withdrawalId, action, reason = null) => {
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}`, {
        action,
        reason
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Withdrawal ${action}d successfully`);
      fetchPendingWithdrawals();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated successfully");
      fetchUsers(userPage, userSearch);
      if (selectedUser?.user_id === userId) {
        setSelectedUser({ ...selectedUser, ...updates });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  // Mock analytics data for charts
  const revenueData = [
    { name: 'Mon', revenue: 45000, bets: 1200 },
    { name: 'Tue', revenue: 52000, bets: 1400 },
    { name: 'Wed', revenue: 48000, bets: 1100 },
    { name: 'Thu', revenue: 61000, bets: 1600 },
    { name: 'Fri', revenue: 55000, bets: 1500 },
    { name: 'Sat', revenue: 72000, bets: 2100 },
    { name: 'Sun', revenue: 68000, bets: 1900 },
  ];

  const gameDistribution = [
    { name: 'Aviator', value: 35 },
    { name: 'Crash', value: 25 },
    { name: 'Slots', value: 20 },
    { name: 'Dice', value: 10 },
    { name: 'Others', value: 10 },
  ];

  const userGrowth = [
    { name: 'Week 1', users: 120 },
    { name: 'Week 2', users: 180 },
    { name: 'Week 3', users: 250 },
    { name: 'Week 4', users: 320 },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Shield className="w-16 h-16 text-[#FF3B30] mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
          <p className="text-[#A1A1AA]">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF3B30] to-[#FF6B00] flex items-center justify-center shadow-lg shadow-[#FF3B30]/20">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Admin Dashboard</h1>
              <p className="text-[#A1A1AA] text-sm">Manage your platform</p>
            </div>
          </div>
          <Button onClick={() => { fetchDashboard(); fetchPendingWithdrawals(); }} variant="outline" size="sm" className="border-white/10">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Users"
              value={dashboard.users?.total || 0}
              subtext={`+${dashboard.users?.new_today || 0} today`}
              icon={Users}
              color="#00E701"
              trend="up"
            />
            <StatCard
              title="Total Wagered"
              value={formatCurrency(dashboard.bets?.total_wagered || 0)}
              subtext={`${(dashboard.bets?.total_bets || 0).toLocaleString()} bets`}
              icon={Gamepad2}
              color="#007AFF"
            />
            <StatCard
              title="Gross Revenue"
              value={formatCurrency(dashboard.bets?.gross_revenue || 0)}
              subtext={`Today: ${formatCurrency(dashboard.bets?.today_wagered || 0)}`}
              icon={TrendingUp}
              color="#FFD600"
              trend={(dashboard.bets?.gross_revenue || 0) > 0 ? "up" : "down"}
            />
            <StatCard
              title="Pending Withdrawals"
              value={dashboard.finances?.pending_withdrawals || 0}
              subtext={formatCurrency(dashboard.finances?.pending_withdrawal_amount || 0)}
              icon={Clock}
              color="#FF3B30"
            />
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#1A1B1F] border border-white/10 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF3B30] data-[state=active]:to-[#FF6B00]">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="withdrawals" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FFD600] data-[state=active]:to-[#FF6B00]">
              <Wallet className="w-4 h-4 mr-2" />
              Withdrawals ({pendingWithdrawals.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#007AFF] data-[state=active]:to-[#00F0FF]">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Revenue Chart */}
              <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#00E701]" />
                  Weekly Revenue
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00E701" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#00E701" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="name" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} tickFormatter={(v) => `₹${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1B1F', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#00E701" fill="url(#revenueGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Game Distribution */}
              <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#007AFF]" />
                  Game Distribution
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPie>
                    <Pie
                      data={gameDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {gameDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1B1F', border: '1px solid #333', borderRadius: '8px' }}
                      formatter={(value) => [`${value}%`, 'Share']}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ color: '#A1A1AA', fontSize: '12px' }}>{value}</span>}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>

              {/* User Growth */}
              <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FFD600]" />
                  User Growth
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="name" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1B1F', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="users" fill="#FFD600" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Quick Stats */}
              <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#FF007A]" />
                  Platform Metrics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard label="Avg Bet Size" value="₹250" change="+12%" />
                  <MetricCard label="Win Rate" value="48.2%" change="-2%" negative />
                  <MetricCard label="Daily Active" value="1,234" change="+8%" />
                  <MetricCard label="Conversion" value="12.5%" change="+3%" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">Pending Withdrawals</h3>
              
              {pendingWithdrawals.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-[#52525B]">No pending withdrawals</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingWithdrawals.map((wd) => (
                    <div key={wd.withdrawal_id} className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-bold text-lg">{formatCurrency(wd.amount)}</span>
                            <Badge variant="outline" className="text-[#FFD600] border-[#FFD600]/50 bg-[#FFD600]/10">
                              {wd.method}
                            </Badge>
                          </div>
                          <p className="text-[#A1A1AA] text-sm">User: {wd.user_id}</p>
                          <p className="text-[#52525B] text-xs">{formatDate(wd.created_at)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleWithdrawal(wd.withdrawal_id, "approve")}
                            className="bg-[#00E701] hover:bg-[#00C201] text-black font-semibold"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              const reason = prompt("Rejection reason:");
                              if (reason) handleWithdrawal(wd.withdrawal_id, "reject", reason);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex-1 min-w-[200px] flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
                  <Search className="w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    placeholder="Search users by email or name..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1, userSearch)}
                    className="bg-transparent border-none outline-none text-white text-sm placeholder:text-white/40 w-full"
                  />
                </div>
                <Button onClick={() => fetchUsers(1, userSearch)} size="sm" className="bg-[#007AFF] hover:bg-[#0066DD]">
                  Search
                </Button>
              </div>

              <div className="space-y-2">
                {users.map((u) => (
                  <div
                    key={u.user_id}
                    onClick={() => setSelectedUser(u)}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      selectedUser?.user_id === u.user_id
                        ? 'bg-[#007AFF]/20 border-2 border-[#007AFF]/50'
                        : 'bg-white/5 border border-transparent hover:bg-white/10 hover:border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center text-black font-bold text-sm">
                          {u.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium">{u.name}</span>
                            {u.is_admin && <Badge className="bg-[#FF3B30] text-white text-[10px]">Admin</Badge>}
                            {u.is_banned && <Badge variant="destructive" className="text-[10px]">Banned</Badge>}
                            <Badge className="bg-[#FFD600]/20 text-[#FFD600] text-[10px]">VIP {u.vip_level}</Badge>
                          </div>
                          <p className="text-[#A1A1AA] text-sm">{u.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#00E701] font-bold font-mono">{formatCurrency(u.balance)}</p>
                        <p className="text-[#52525B] text-xs">Wagered: {formatCurrency(u.total_wagered)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={userPage <= 1}
                  onClick={() => fetchUsers(userPage - 1, userSearch)}
                  className="border-white/10"
                >
                  Previous
                </Button>
                <span className="text-white/60 py-2 px-4 text-sm">
                  Page {userPage} of {totalUserPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={userPage >= totalUserPages}
                  onClick={() => fetchUsers(userPage + 1, userSearch)}
                  className="border-white/10"
                >
                  Next
                </Button>
              </div>
            </div>

            {/* Selected User Details */}
            {selectedUser && (
              <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10 mt-4">
                <h3 className="text-lg font-bold text-white mb-4">User Details: {selectedUser.name}</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-white/5">
                    <p className="text-[#A1A1AA] text-xs mb-1">Balance</p>
                    <p className="text-[#00E701] font-bold text-lg">{formatCurrency(selectedUser.balance)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5">
                    <p className="text-[#A1A1AA] text-xs mb-1">Total Wagered</p>
                    <p className="text-white font-bold text-lg">{formatCurrency(selectedUser.total_wagered)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5">
                    <p className="text-[#A1A1AA] text-xs mb-1">Total Won</p>
                    <p className="text-white font-bold text-lg">{formatCurrency(selectedUser.total_won)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5">
                    <p className="text-[#A1A1AA] text-xs mb-1">VIP Level</p>
                    <p className="text-[#FFD600] font-bold text-lg">Level {selectedUser.vip_level}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      const amount = prompt("Add balance amount:");
                      if (amount) updateUser(selectedUser.user_id, { balance: selectedUser.balance + parseFloat(amount) });
                    }}
                    className="bg-[#00E701] hover:bg-[#00C201] text-black"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Add Balance
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const level = prompt("Set VIP level (1-6):");
                      if (level) updateUser(selectedUser.user_id, { vip_level: parseInt(level) });
                    }}
                    className="bg-[#FFD600] hover:bg-[#E6C200] text-black"
                  >
                    <Crown className="w-4 h-4 mr-1" />
                    Set VIP
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedUser.is_admin ? "destructive" : "outline"}
                    onClick={() => updateUser(selectedUser.user_id, { is_admin: !selectedUser.is_admin })}
                    className={!selectedUser.is_admin ? "border-white/10" : ""}
                  >
                    <Shield className="w-4 h-4 mr-1" />
                    {selectedUser.is_admin ? "Remove Admin" : "Make Admin"}
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedUser.is_banned ? "outline" : "destructive"}
                    onClick={() => {
                      if (selectedUser.is_banned) {
                        updateUser(selectedUser.user_id, { is_banned: false });
                      } else {
                        const reason = prompt("Ban reason:");
                        if (reason) updateUser(selectedUser.user_id, { is_banned: true, ban_reason: reason });
                      }
                    }}
                    className={selectedUser.is_banned ? "border-white/10" : ""}
                  >
                    <Ban className="w-4 h-4 mr-1" />
                    {selectedUser.is_banned ? "Unban" : "Ban User"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const StatCard = ({ title, value, subtext, icon: Icon, color, trend }) => (
  <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10 hover:border-white/20 transition-colors">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[#A1A1AA] text-sm">{title}</span>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white mb-1">{value}</p>
    <div className="flex items-center gap-1">
      {trend === "up" && <ArrowUpRight className="w-3 h-3 text-[#00E701]" />}
      {trend === "down" && <ArrowDownRight className="w-3 h-3 text-[#FF3B30]" />}
      <span className="text-[#52525B] text-xs">{subtext}</span>
    </div>
  </div>
);

const MetricCard = ({ label, value, change, negative }) => (
  <div className="p-3 rounded-lg bg-white/5">
    <p className="text-[#A1A1AA] text-xs mb-1">{label}</p>
    <p className="text-white font-bold text-lg">{value}</p>
    <p className={`text-xs ${negative ? 'text-[#FF3B30]' : 'text-[#00E701]'}`}>{change}</p>
  </div>
);

export default AdminPage;
